import { Outlet, type ParsedLocation, createRootRouteWithContext, redirect } from '@tanstack/react-router';
import { CommandPalette } from '@/features/command-palette';
import { GlobalAiChatWidget } from '@/features/global-ai-chat';
import { useAiChatFabVisible } from '@/hooks/useAiChatFabVisible';
import { featureFlags } from '@/lib/env';
import { GlobalConfirmDialog } from '../components/Dialog/GlobalConfirmDialog/GlobalConfirmDialog';
import { NotFound } from '../components/Error/NotFound';
import { FullScreenOverlay } from '../components/Loading';
import { useBfcacheSessionCheck } from '../lib/auth/hooks';
import { authService } from '../lib/auth/ory/service';
import type { queryClient } from '../lib/queryClient';
import { useGlobalLoadingStore } from '../lib/store/loading';
import { GlobalShortcuts } from './-components/GlobalShortcuts';

/**
 * 認証チェックを実行する
 *
 * whoami APIを呼び出して認証状態をチェックし、
 * 未認証またはMFA認証が必要な場合はサインインページにリダイレクトする
 */
async function checkAuthentication(location: ParsedLocation<Record<string, never>>): Promise<void> {
  // /auth配下および /me（認証ポータル）の場合は認証チェックをスキップ
  // /me は独自の beforeLoad で Ory 認証チェックを行い、
  // セッション検証（GET /v2/session）の 403 を回避する
  //
  // /oauth2/* は IdP リダイレクト先で login_challenge を保ったまま遷移する必要があるため
  // 認証チェックをスキップし、各ルート側で個別に判定する
  if (location.pathname.startsWith('/auth') || location.pathname.startsWith('/me') || location.pathname.startsWith('/oauth2/')) {
    return;
  }
  const status = await authService.getAuthenticationStatus();

  // 未認証またはMFA認証が必要な場合はサインインページにリダイレクト
  if (status === 'unauthenticated' || status === 'requires_mfa') {
    throw redirect({
      to: '/auth/sign-in',
      search: {
        redirect: location.pathname
      },
      replace: true
    });
  }
}
export interface RouterContext {
  queryClient: typeof queryClient;
}
export const Route = createRootRouteWithContext<RouterContext>()({
  component: Root,
  beforeLoad: async ({
    location
  }) => {
    await checkAuthentication(location);
  },
  notFoundComponent: NotFound
});
function Root() {
  const {
    isLoading,
    message
  } = useGlobalLoadingStore();
  const isAiChatWidgetVisible = useAiChatFabVisible();

  // bfcache対策: ブラウザバックでページが復元された場合にセッション状態を再チェック
  useBfcacheSessionCheck();
  return <>
      <GlobalShortcuts />
      <GlobalConfirmDialog />

      {/* 開発者用のショートカット。Cmd+Kでコマンドパレットを開く。 */}
      {featureFlags.check('NEW_FEATURE') && <CommandPalette />}
      {isAiChatWidgetVisible && <GlobalAiChatWidget />}

      {/* 組織切り替えなどのグローバルな状態変更時はFullScreenOverlayを使用してユーザー操作をブロック */}
      {isLoading && <FullScreenOverlay message={message} />}
      <Outlet />
    </>;
}